/**
 * Client-safe model definitions and the routes on which cubic offers them. No credential reads,
 * session authorization or runtime configuration belong in the catalog. `../models.ts` resolves
 * a stored choice; an entry leaving this catalog must not change who pays for an existing session.
 */
import type {
  AiSubscriptionModelDescriptor,
  AiSubscriptionProvider,
} from '../ai-subscriptions/providers';

import {
  AI_SUBSCRIPTION_DESCRIPTORS,
  AI_SUBSCRIPTION_PROVIDERS,
} from '../ai-subscriptions/providers';

/** The efforts a session model can run at, from the least reasoning to the most. */
export const CODE_SESSION_REASONING_EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;

/**
 * Reasoning effort a session model runs at. The sandbox sends it on every
 * request and the model picker shows it, so the label always matches the
 * request: provider defaults differ by route (Baseten runs GLM 5.3 at `high`,
 * Modal at the chat template's `max`) and by model generation.
 */
export type CodeSessionReasoningEffort = (typeof CODE_SESSION_REASONING_EFFORTS)[number];

/** A model cubic's Vercel AI Gateway serves. */
export type CodeSessionModel = {
  /**
   * Which credential pays for the model's requests, and so which OpenCode
   * provider serves them: cubic's Gateway here, or the session owner's own
   * AI subscription for a `CodeSessionAiSubscriptionModel`.
   */
  route: 'gateway';
  modelId: string;
  /** Hide from new-session choices without removing existing sessions' runtime settings. */
  hiddenFromPicker?: boolean;
  providers: readonly string[];
  zeroDataRetention?: boolean;
  disallowPromptTraining?: boolean;
  /** The effort a session runs the model at unless its stored model names another. */
  reasoningEffort: CodeSessionReasoningEffort;
  /**
   * Whether a new session may run the model at any effort cubic names, from low to max
   * (`CODE_SESSION_REASONING_EFFORTS`); without it, only at `reasoningEffort`.
   */
  offersEveryEffort?: boolean;
  /**
   * Whether the model reads images and PDFs, per its input modalities on
   * models.dev. A session on a model without it is refused image and PDF
   * attachments; other files reach its agent as sandbox paths either way.
   */
  acceptsImages: boolean;
};

export const CODE_SESSION_SPEEDS = ['standard', 'fast'] as const;

export type CodeSessionSpeed = (typeof CODE_SESSION_SPEEDS)[number];

/**
 * A model a person's own AI subscription runs, through the sandbox plugin on
 * the provider's own OpenCode provider (the descriptor's `openCodeProviderId`).
 * Not a Gateway model: it has no upstreams and no output limit.
 */
export type CodeSessionAiSubscriptionModel = AiSubscriptionModelDescriptor & {
  route: 'ai_subscription';
  provider: AiSubscriptionProvider;
  speed: CodeSessionSpeed;
};

/** A model of either route. */
export type CodeSessionCatalogModel = CodeSessionModel | CodeSessionAiSubscriptionModel;

/** The curated choices, distinct from the catalog that keeps existing sessions running. */
export function isOfferedCodeSessionModel<Entry extends CodeSessionCatalogModel>(
  entry: Entry,
): entry is Entry & { hiddenFromPicker?: false } {
  return entry.hiddenFromPicker !== true;
}

/**
 * The default interactive model. The
 * Gateway serves it only from first-party OpenAI, with ZDR and no-training
 * (checked 2026-09-29, its release day), so it takes the GPT-6 models' route.
 */
export const CODE_SESSION_GPT_61_SOL_MODEL = {
  route: 'gateway',
  modelId: 'openai/gpt-6.1-sol',
  providers: ['openai'],
  zeroDataRetention: true,
  reasoningEffort: 'high',
  offersEveryEffort: true,
  acceptsImages: true,
} as const satisfies CodeSessionModel;

/** A picker choice, and what a pull request's demo session runs (`pull-request-demo/start.ts`). */
export const CODE_SESSION_GPT_6_LUNA_MODEL = {
  route: 'gateway',
  modelId: 'openai/gpt-6-luna',
  providers: ['openai'],
  zeroDataRetention: true,
  reasoningEffort: 'max',
  offersEveryEffort: true,
  acceptsImages: true,
} as const satisfies CodeSessionModel;

/**
 * Code-session models and their allowed Vercel Gateway upstreams.
 * Checked against the Gateway on 2026-09-28: first-party OpenAI serves every
 * GPT-6 model under zero data retention. Bedrock offers no ZDR for them; Azure
 * does, but at that check it filed GPT's reasoning under `azure`, which the
 * sandbox plugin's Gateway replay did not restore. That replay is gone (OpenCode
 * now carries `/v1/responses` reasoning natively as `encrypted_content`), so
 * re-check Azure's reasoning round trip before allowing it.
 * Keep these session choices separate from the shared auto-fix model defaults.
 */
export const CODE_SESSION_OPENAI_MODELS = [
  {
    route: 'gateway',
    modelId: 'openai/gpt-6-astra',
    providers: ['openai'],
    zeroDataRetention: true,
    reasoningEffort: 'medium',
    offersEveryEffort: true,
    acceptsImages: true,
  },
  {
    route: 'gateway',
    modelId: 'openai/gpt-6-sol',
    hiddenFromPicker: true,
    providers: ['openai'],
    zeroDataRetention: true,
    reasoningEffort: 'medium',
    offersEveryEffort: true,
    acceptsImages: true,
  },
  CODE_SESSION_GPT_61_SOL_MODEL,
  CODE_SESSION_GPT_6_LUNA_MODEL,
] as const satisfies readonly CodeSessionModel[];

/**
 * Claude models keep the coding agent's Bedrock-first route with Anthropic as
 * the only fallback. Both think adaptively; Opus 5.5 cannot turn thinking off.
 */
export const CODE_SESSION_ANTHROPIC_MODELS = [
  {
    route: 'gateway',
    modelId: 'anthropic/claude-sonnet-5.5',
    providers: ['bedrock', 'anthropic'],
    zeroDataRetention: true,
    reasoningEffort: 'high',
    offersEveryEffort: true,
    acceptsImages: true,
  },
  {
    route: 'gateway',
    modelId: 'anthropic/claude-opus-5.5',
    providers: ['bedrock', 'anthropic'],
    zeroDataRetention: true,
    reasoningEffort: 'medium',
    offersEveryEffort: true,
    acceptsImages: true,
  },
  {
    route: 'gateway',
    modelId: 'anthropic/claude-sonnet-5',
    hiddenFromPicker: true,
    providers: ['bedrock', 'anthropic'],
    zeroDataRetention: true,
    reasoningEffort: 'high',
    offersEveryEffort: true,
    acceptsImages: true,
  },
] as const satisfies readonly CodeSessionModel[];

// `only` + `order` routing consume this list: Baseten leads, Modal is the only
// fallback, and GLM cannot silently route to an unlisted provider. Both offer
// ZDR and no-training on the Gateway catalog (checked 2026-09-21); Baseten was
// promoted because Modal decoded at ~15 tok/s p50 against Baseten's ~110.
// It offers no other effort: Modal takes every effort with no consistent effect,
// and whether Baseten honors one is unverified.
export const CODE_SESSION_GLM_MODEL = {
  route: 'gateway',
  modelId: 'zai/glm-5.3',
  providers: ['baseten', 'modal'],
  zeroDataRetention: true,
  disallowPromptTraining: true,
  reasoningEffort: 'high',
  acceptsImages: false,
} as const satisfies CodeSessionModel;
export const CODE_SESSION_ROUTED_MODELS = [
  CODE_SESSION_GLM_MODEL,
  ...CODE_SESSION_ANTHROPIC_MODELS,
  ...CODE_SESSION_OPENAI_MODELS,
] as const;

/**
 * Models a session can run on its owner's own AI subscription: each provider's
 * descriptor lists its own (`models`). They stay out of every shared list
 * (`CODE_SESSION_ROUTED_MODELS`, `CODE_SESSION_OFFERED_MODELS`, the defaults),
 * so no unattended creator and no MCP `start_session` picks one; an owner's
 * agent may inherit one. DeepSWE's lookup of routed models by Gateway id never
 * finds one. Each is followed by its fast form, even where its descriptor has
 * no `fastTier`: a session that went fast before then keeps its model's effort
 * until its owner turns Fast mode off.
 */
export const CODE_SESSION_AI_SUBSCRIPTION_MODELS: readonly CodeSessionAiSubscriptionModel[] =
  AI_SUBSCRIPTION_PROVIDERS.flatMap((provider) =>
    AI_SUBSCRIPTION_DESCRIPTORS[provider].models.flatMap((descriptor) => {
      const standard: CodeSessionAiSubscriptionModel = {
        ...descriptor,
        route: 'ai_subscription',
        provider,
        speed: 'standard',
      };
      return [standard, { ...standard, speed: 'fast' as const }];
    }),
  );
